import { computed, unref, ComputedRef, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BigNumber from 'bignumber.js';
import ItemSummary from '@/models/entities/items/item-summary';
import {
  isAdditionalScanCode,
  isSoldByWeight,
  isSoldByAverageWeight,
  isSoldByRandomWeight,
  hasWeightAbbreviation,
  isPriceDivided,
  unitPrice,
  suggestedPrice,
  actualPrice,
  hasActualPrice,
  averageWeight,
  discountAmount,
  isDiscounted,
  isDiscountedInCart,
} from '@/utils/item-utils';
import { currency } from '@/utils/formatters';

export type ItemPriceInput = Ref<ItemSummary> | ItemSummary;

export interface UseItemPriceReturn {
  isAdditionalScanCode: ComputedRef<boolean>;
  isSoldByWeight: ComputedRef<boolean>;
  isSoldByAverageWeight: ComputedRef<boolean>;
  isSoldByRandomWeight: ComputedRef<boolean>;
  hasWeightAbbreviation: ComputedRef<boolean>;
  isPriceDivided: ComputedRef<boolean>;
  hasActualPrice: ComputedRef<boolean>;
  isDiscounted: ComputedRef<boolean>;
  isDiscountedInCart: ComputedRef<boolean>;
  hasBuyDown: ComputedRef<boolean>;

  unitPrice: ComputedRef<BigNumber>;
  suggestedPrice: ComputedRef<BigNumber>;
  actualPrice: ComputedRef<BigNumber>;
  averageWeight: ComputedRef<BigNumber>;
  discountAmount: ComputedRef<BigNumber>;

  unitDisplay: ComputedRef<string>;
  actualPriceDisplay: ComputedRef<string>;
  suggestedPriceDisplay: ComputedRef<string>;
  averagePriceDisplay: ComputedRef<string>;
  suggestedAveragePriceDisplay: ComputedRef<string>;
  estimatedEachDisplay: ComputedRef<string>;
  avgWeightTooltipEstimate: ComputedRef<string>;
}

export default function useItemPrice(item: ItemPriceInput): UseItemPriceReturn {
  const { t } = useI18n();
  const resolveItem = (): ItemSummary => unref(item);
  const wrap = <T>(fn: (resolved: ItemSummary) => T): ComputedRef<T> =>
    computed(() => fn(resolveItem()));

  const isAdditionalScanCodeRef = wrap(isAdditionalScanCode);
  const isSoldByWeightRef = wrap(isSoldByWeight);
  const isSoldByAverageWeightRef = wrap(isSoldByAverageWeight);
  const isSoldByRandomWeightRef = wrap(isSoldByRandomWeight);
  const hasWeightAbbreviationRef = wrap(hasWeightAbbreviation);
  const isPriceDividedRef = wrap(isPriceDivided);
  const hasActualPriceRef = wrap(hasActualPrice);
  const isDiscountedRef = wrap(isDiscounted);
  const isDiscountedInCartRef = wrap(isDiscountedInCart);
  const hasBuyDown = computed(() => Boolean(resolveItem().buyDown));

  const unitPriceRef = wrap(unitPrice);
  const suggestedPriceRef = wrap(suggestedPrice);
  const actualPriceRef = wrap(actualPrice);
  const averageWeightRef = wrap(averageWeight);
  const discountAmountRef = wrap(discountAmount);

  const unitDisplay = computed(() =>
    hasWeightAbbreviationRef.value ? resolveItem().weightProfile.abbrv : t('eachForwardSlashAbbr'),
  );

  const formatXForY = (x: string | number, y: string | number) => t('xForY', { x, y });
  const formatApproximate = (price: string) => t('approximateEach', { price });

  const suggestedPriceDisplay = computed(() =>
    formatXForY(currency(suggestedPriceRef.value.toNumber()), unitDisplay.value),
  );

  const actualPriceDisplay = computed(() => {
    const actualPriceFormatted = currency(actualPriceRef.value.toNumber());
    if (isPriceDividedRef.value) {
      return formatXForY(resolveItem().actualPriceDivider, actualPriceFormatted);
    }
    return formatXForY(actualPriceFormatted, unitDisplay.value);
  });

  const averagePriceFor = (priceRef: ComputedRef<BigNumber>) => {
    if (!isSoldByAverageWeightRef.value) {
      return '';
    }
    const averagePriceValue = priceRef.value.multipliedBy(averageWeightRef.value);
    return formatApproximate(currency(averagePriceValue.toNumber()));
  };

  const averagePriceDisplay = computed(() => averagePriceFor(unitPriceRef));
  const suggestedAveragePriceDisplay = computed(() => averagePriceFor(suggestedPriceRef));

  const estimatedEachDisplay = computed(() => {
    if (!isSoldByAverageWeightRef.value) return '';
    const est = unitPriceRef.value.multipliedBy(averageWeightRef.value);
    return t('estimatedEach', { price: currency(est.toNumber()) });
  });

  const avgWeightTooltipEstimate = computed(() => {
    if (!isSoldByAverageWeightRef.value) return '';
    const unit = unitDisplay.value;
    const est = currency(unitPriceRef.value.multipliedBy(averageWeightRef.value).toNumber());
    const unitPriceFormatted = `${currency(unitPriceRef.value.toNumber())}/${unit}`;
    const weight = `${averageWeightRef.value.toFixed(2)} ${unit}`;
    return t('avgWeightTooltipEstimate', { est, unitPrice: unitPriceFormatted, weight });
  });

  return {
    isAdditionalScanCode: isAdditionalScanCodeRef,
    isSoldByWeight: isSoldByWeightRef,
    isSoldByAverageWeight: isSoldByAverageWeightRef,
    isSoldByRandomWeight: isSoldByRandomWeightRef,
    hasWeightAbbreviation: hasWeightAbbreviationRef,
    isPriceDivided: isPriceDividedRef,
    hasActualPrice: hasActualPriceRef,
    isDiscounted: isDiscountedRef,
    isDiscountedInCart: isDiscountedInCartRef,
    hasBuyDown,

    unitPrice: unitPriceRef,
    suggestedPrice: suggestedPriceRef,
    actualPrice: actualPriceRef,
    averageWeight: averageWeightRef,
    discountAmount: discountAmountRef,

    unitDisplay,
    actualPriceDisplay,
    suggestedPriceDisplay,
    averagePriceDisplay,
    suggestedAveragePriceDisplay,
    estimatedEachDisplay,
    avgWeightTooltipEstimate,
  };
}
